import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Pencil, Plus } from "lucide-react";
import { RichTextEditorSheet } from "@/components/RichTextEditorSheet";

interface CoverLetterSectionProps {
  /** Current markdown content of the cover letter */
  value: string;
  /** Called when the cover letter content changes */
  onChange: (value: string) => void;
  /** Whether the editor is in read-only mode */
  readOnly?: boolean;
}

/**
 * Cover letter editing section for the quote editor.
 * Provides a sheet-based editor with markdown textarea support.
 * @param props - CoverLetterSectionProps
 * @returns Cover letter section element
 */
export function CoverLetterSection({
  value,
  onChange,
  readOnly = false,
}: CoverLetterSectionProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localValue, setLocalValue] = useState("");

  useEffect(() => {
    if (sheetOpen) {
      setLocalValue(value);
    }
  }, [sheetOpen, value]);

  /**
   * Applies the local edits and closes the sheet.
   */
  const handleApply = useCallback(() => {
    onChange(localValue);
    setSheetOpen(false);
  }, [localValue, onChange]);

  return (
    <>
      <div className="px-3 sm:px-6 py-4">
        <h3 className="text-base sm:text-lg font-semibold mb-4">
          Cover Letter
        </h3>
        {value ? (
          <div className="border rounded-md p-3 bg-muted/30">
            <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
              {value}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => setSheetOpen(true)}
            >
              {readOnly ? (
                <>
                  <FileText className="h-4 w-4 mr-1" />
                  View
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </>
              )}
            </Button>
          </div>
        ) : (
          !readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSheetOpen(true)}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Cover Letter
            </Button>
          )
        )}
      </div>

      <RichTextEditorSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="Cover Letter"
        value={localValue}
        onChange={setLocalValue}
        placeholder="Enter cover letter content"
        readOnly={readOnly}
        footer={
          readOnly ? (
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApply}>Done</Button>
            </>
          )
        }
      />
    </>
  );
}
